import { ADMIN_PERMISSIONS, hasAdminPermission, type AdminPermission } from '@/contracts';
import {
  Briefcase,
  Building2,
  ChartLine,
  CreditCard,
  FileCheck2,
  LayoutDashboard,
  LifeBuoy,
  Settings,
  ShieldCheck,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/**
 * The admin console's information architecture, exactly as the approved prototype groups
 * it: monitoring, then the two manual-review queues (C5), then the record lists, then
 * settings.
 *
 * One structure feeds four things — the sidebar, the page title and hint in the sticky
 * header, the active-route highlight, and the permission each route demands. Keeping
 * them in one array is what stops the header from saying "الكوادر" while the sidebar
 * highlights "الاشتراكات", and it is why route protection cannot drift from the menu:
 * hiding an entry and refusing the URL read the same field.
 *
 * `key` is a translation key under `admin.nav.items` / `admin.nav.hints`, never a label.
 */
export interface AdminNavItem {
  key: string;
  href: string;
  icon: LucideIcon;
  /** `admin.nav.badges.<badgeKey>` counts are supplied by the layout, not hard-coded. */
  badgeKey?: 'verification' | 'documents';
  /**
   * What an admin must hold to open this route. Absent means any authenticated admin —
   * the overview, the reports and the component gallery are readable by the whole team.
   */
  permission?: AdminPermission;
}

export interface AdminNavGroup {
  key: string;
  items: readonly AdminNavItem[];
}

export const ADMIN_NAV: readonly AdminNavGroup[] = [
  {
    key: 'monitoring',
    items: [
      { key: 'overview', href: '/', icon: LayoutDashboard },
      { key: 'reports', href: '/reports', icon: ChartLine },
    ],
  },
  {
    key: 'review',
    items: [
      {
        key: 'verification',
        href: '/verification',
        icon: ShieldCheck,
        badgeKey: 'verification',
        permission: ADMIN_PERMISSIONS.ORGS_VERIFY,
      },
      {
        key: 'documents',
        href: '/documents',
        icon: FileCheck2,
        badgeKey: 'documents',
        permission: ADMIN_PERMISSIONS.DOCUMENTS_AUDIT,
      },
      // No badge: the sidebar counts come from `/admin/moderation/pending-counts`,
      // which knows the two review queues and nothing else. The inbox shows its own
      // state through its status tabs.
      {
        key: 'support',
        href: '/support',
        icon: LifeBuoy,
        permission: ADMIN_PERMISSIONS.SUPPORT_MANAGE,
      },
    ],
  },
  {
    key: 'records',
    items: [
      {
        key: 'talents',
        href: '/talents',
        icon: Users,
        permission: ADMIN_PERMISSIONS.TALENTS_MANAGE,
      },
      {
        key: 'organizations',
        href: '/organizations',
        icon: Building2,
        permission: ADMIN_PERMISSIONS.ORGS_MANAGE,
      },
      { key: 'subscriptions', href: '/subscriptions', icon: CreditCard },
      { key: 'roles', href: '/roles', icon: Briefcase },
    ],
  },
  {
    key: 'system',
    items: [
      {
        key: 'settings',
        href: '/settings',
        icon: Settings,
        permission: ADMIN_PERMISSIONS.ADMINS_MANAGE,
      },
    ],
  },
];

const ALL_ITEMS = ADMIN_NAV.flatMap((group) => group.items);

/**
 * The nav item a pathname belongs to.
 *
 * Longest match wins, so `/admin/talents/abc` resolves to the talents item rather than
 * to the overview at `/admin` — which every route would otherwise prefix-match.
 */
export function activeNavItem(pathname: string): AdminNavItem | undefined {
  const norm = pathname.replace(/^\/admin(?=\/|$)/, '') || '/';
  const matches = ALL_ITEMS.filter(
    (item) => item.href === norm || (item.href !== '/' && norm.startsWith(`${item.href}/`)),
  );
  if (matches.length > 0) {
    return matches.sort((a, b) => b.href.length - a.href.length)[0];
  }
  return ALL_ITEMS.find((item) => item.href === '/');
}

/**
 * Whether this admin may open this console path.
 *
 * Resolved through `activeNavItem`, so a detail route inherits its list's permission —
 * `/admin/documents/abc` needs exactly what `/admin/documents` needs — and a path that
 * matches nothing falls back to the overview, which every admin can read. The layout
 * calls this on the URL the request actually asked for, which is what makes typing the
 * address bar no different from clicking a hidden link.
 */
export function canAccessAdminPath(pathname: string, permissions: readonly string[]): boolean {
  const required = activeNavItem(pathname)?.permission;
  return !required || hasAdminPermission(permissions, required);
}

/**
 * The nav as this admin sees it. A group whose every item is out of reach disappears
 * with its heading rather than leaving an empty section label behind.
 */
export function visibleAdminNav(permissions: readonly string[] = []): AdminNavGroup[] {
  return ADMIN_NAV.map((group) => ({
    ...group,
    items: group.items.filter(
      (item) => !item.permission || hasAdminPermission(permissions, item.permission),
    ),
  })).filter((group) => group.items.length > 0);
}
